import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRightIcon, RepeatIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { useFeatures } from '../../hooks/useFeatures';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import type { HomePage } from '../../core/types/pages';

/**
 * "Opbouwen": wat er gebeurt nádat je een protocol kiest. Het protocol zelf verkoopt het blok
 * Kiezen erboven (per klacht, met prijs); hier geen tweede rij protocolkaarten, maar de
 * belofte erachter: huidplan, bijsturen, opnieuw kijken. Als tijdlijn, want het is een volgorde;
 * de lijn loopt mee met het scrollen. Eronder het abonnement (sluit aan op "bestel bij").
 * De cadeaustaffel staat hier bewust níét: die geldt voor elke bestelling en staat daarom bij
 * "Liever met één product beginnen?" (HomeShelf).
 */
type Props = {
  /**
   * Kop en zin; standaard die van Home. Protocollen geeft eigen woorden mee (zonder label en
   * zonder link naar zichzelf). De momenten op de tijdlijn komen altijd uit Home: één bron.
   */
  copy?: Pick<HomePage['protocols'], 'eyebrow' | 'heading' | 'body' | 'cta'>;
  /** Rustig (Protocollen): de lijn staat al vol en loopt niet mee met scrollen. */
  still?: boolean;
  /** Ankertje van de kop. */
  titleId?: string;
};
export function HomeProtocols({
  copy: ownCopy,
  still = false,
  titleId = 'home-protocols'
}: Props = {}) {
  const pages = usePages();
  const {
    subscriptions
  } = useFeatures();
  const reduce = useReducedMotion() || still;
  const copy = ownCopy ?? pages.home.protocols;
  const lineRef = useRef<HTMLOListElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: lineRef,
    offset: ['start 75%', 'end 60%']
  });
  const milestones = pages.home.protocols.milestones.filter(m => m.title);
  // Op Protocollen staat het abonnement er direct onder als eigen blok: daar geen regel.
  const showSubscription = subscriptions && !ownCopy;
  return <Section tone="sand" labelledBy={titleId} contained={false} data-id="element-7434" data-mp="11awknumla8ql">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7435" data-mp="wcmiv16d09w0">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7436" data-mp="rf3502dkpz4l">
          <div className="lg:col-span-5" data-id="element-7437" data-mp="rgxgkq2ygew0">
            {copy.eyebrow && <p className={`${eyebrowCls} text-clay`} data-id="element-7438" data-mp="mgrwbuz0luny">{fill(copy.eyebrow)}</p>}
            <h2 id={titleId} className={`${copy.eyebrow ? 'mt-4' : ''} ${sectionHeading} text-ink`} data-id="element-7439" data-mp="hohylsrnrqb4">
              <HeadingText heading={copy.heading} data-id="element-7440" data-mp="dmwscq9mqa9w" />
            </h2>
            {copy.body && <p className="mt-6 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7441" data-mp="8oli4eff9xr5">{fill(copy.body)}</p>}
            {copy.cta && <Link to="/routines" className={`mt-6 ${quietLink} text-ink`} data-id="element-7442" data-mp="3o77comfs9pf">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7443" data-mp="3uwusp4kofwwz" />
              </Link>}
          </div>

          {milestones.length > 0 && <ol ref={lineRef} className="relative lg:col-span-7 lg:col-start-6 lg:pl-4" data-id="element-7444" data-mp="3ppwq2ds8zh6z">
              {/* De lijn van de tijdlijn; vult zich terwijl je leest. Bij "minder beweging" staat hij vol. */}
              <span aria-hidden="true" className="absolute bottom-3 left-[7px] top-3 w-px bg-ink/15 lg:left-[23px]" data-id="element-7445" data-mp="3jrdjoy7nmti">
                <motion.span style={reduce ? undefined : {
              scaleY: scrollYProgress
            }} className="absolute inset-0 origin-top bg-clay" data-id="element-7446" data-mp="322g5xyf4ysr4" />
              </span>
              {milestones.map((m, i) => <li key={`${i}-${m.title}`} className="relative pb-12 pl-10 last:pb-0 sm:pb-14" data-id="element-7447" data-mp="3byjcdgh0tdjv" data-mpi={i}>
                  <span aria-hidden="true" className="absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border border-clay bg-sand lg:left-4" data-id="element-7448" data-mp="3gqt6z9t2z1sh" />
                  <div className="lg:pl-6" data-id="element-7449" data-mp="2o9qkpw7flrv2">
                    {m.when && <p className="text-[14px] text-clay" data-id="element-7450" data-mp="2o85sid8lal0d">{fill(m.when)}</p>}
                    <h3 className="mt-1.5 type-title text-ink" data-id="element-7451" data-mp="tujrvukax9n8">{fill(m.title)}</h3>
                    {m.body && <p className="mt-3 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-7452" data-mp="ov6wglfd6u91">{fill(m.body)}</p>}
                  </div>
                </li>)}
            </ol>}
        </div>

        {/* Het abonnement als één regel: de uitleg zelf staat bij Protocollen en in de webshop. */}
        {showSubscription && <Link to="/routines" className="group mt-16 flex flex-col gap-2 border-t border-line pt-7 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-id="element-7453" data-mp="1ci6ohacrcvzc">
            <span className="flex items-start gap-3 text-[17px] leading-relaxed text-ink" data-id="element-7454" data-mp="1mifj4jr3q9cm">
              <RepeatIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7455" data-mp="1hit8mi2nr77i" />
              <span data-id="element-7456" data-mp="3oalndopp84gs">
                <span className="type-name" data-id="element-7457" data-mp="3oa2fl0triwgh">{pages.subscription.name}</span>
                <span className="text-muted" data-id="element-7458" data-mp="3jbhg53mc4gt3"> · {pages.subscription.claim}</span>
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-2 pl-7 text-[15px] text-ink group-hover:underline group-hover:underline-offset-[6px] sm:pl-0" data-id="element-7459" data-mp="1ysx67l7z0fkn">
              Zo werkt het abonnement
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7460" data-mp="1p22iecf2t7x3" />
            </span>
          </Link>}
      </div>
    </Section>;
}